import { useEffect, useRef } from 'react';
import { snapOrFromTo } from 'animations/snapTween';
import { useAnimationPreferences } from 'contexts/AnimationPreferencesContext';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import { useTableSnap } from 'hooks/useTableSnap';

export const useSeatAnimation = (
  isWinner: boolean,
  isTurn: boolean,
  isPortrait: boolean,
  seatRef: React.RefObject<HTMLDivElement>,
  turnGlowRef: React.RefObject<HTMLDivElement>,
  opponentGlowRef: React.RefObject<HTMLDivElement>
) => {
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const turnTweenRef = useRef<gsap.core.Tween | null>(null);
  const winTlRef = useRef<gsap.core.Timeline | null>(null);
  const prevIsWinner = useRef(false);
  const shouldSnap = useTableSnap();
  const { reduceAnimations } = useAnimationPreferences();

  // Win celebration animation. Skipped while the table is snapping: arriving at
  // a table where a hand has already been won must not replay the 2.8s seat
  // celebration for it (see hooks/useTableSnap).
  useEffect(() => {
    if (isHandHistorySnapshot) {
      if (seatRef.current) gsap.set(seatRef.current, { opacity: 1, scale: 1 });
      prevIsWinner.current = isWinner;
      return;
    }

    if (isWinner && !prevIsWinner.current && seatRef.current && !shouldSnap()) {
      winTlRef.current = gsap.timeline();
      winTlRef.current
        .to(seatRef.current, {
          // EN-10520 — recognition, not drama. The old 1.5x/1.3x pop made a
          // winning seat swallow its neighbours (and, in a 4-table grid cell,
          // a good part of the felt). The glow ring below is what actually
          // marks the winner; the scale is now just a nudge that reads as
          // "this one" without covering anything.
          scale: isPortrait ? 1.12 : 1.08,
          duration: reduceAnimations ? 0.15 : 0.4,
          ease: 'back.out(1.2)'
        })
        .to(seatRef.current, {
          // Reduced celebration fits inside the pot's 0.8s chip flight.
          duration: reduceAnimations ? 0.5 : 2
        })
        .to(seatRef.current, {
          scale: 1,
          duration: reduceAnimations ? 0.15 : 0.4,
          ease: 'power2.inOut'
        });
    }
    prevIsWinner.current = isWinner;

    // Kill the (potentially 2.8s-long) celebration timeline on re-run/unmount so
    // a seat that unmounts mid-celebration doesn't orphan it against a detached
    // node. Crucially, ALSO snap the seat back to scale 1: the celebration scales
    // seatRef up (see the timeline above) and only returns to 1 via its final keyframe,
    // so killing it mid-flight (a dep change, a remount, or a tab backgrounded
    // while it was frozen) would otherwise strand the whole opponent block
    // enlarged — the reported "opponent profile stuck at Largest after idling /
    // opening another tab" bug.
    return () => {
      if (winTlRef.current) {
        winTlRef.current.kill();
        winTlRef.current = null;
        if (seatRef.current) gsap.set(seatRef.current, { scale: 1 });
      }
    };
    // shouldSnap is read inside the effect on purpose — see hooks/useTableSnap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandHistorySnapshot, isWinner, isPortrait, seatRef, reduceAnimations]);

  // Safety net for background tabs: gsap tweens are throttled while a tab is
  // hidden, so a win celebration frozen mid-flight can come back stuck at its
  // enlarged scale without any dep change to trigger the cleanup above.
  // When the tab regains visibility and no celebration is actively running,
  // snap the seat back to its natural scale.
  useEffect(() => {
    if (isHandHistorySnapshot) return;

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const celebrating = !!winTlRef.current && winTlRef.current.isActive();
      if (!celebrating && seatRef.current) {
        gsap.set(seatRef.current, { scale: 1 });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [isHandHistorySnapshot, seatRef]);

  // Turn/winner glow animation
  useEffect(() => {
    if (turnTweenRef.current) {
      turnTweenRef.current.kill();
      turnTweenRef.current = null;
    }

    [turnGlowRef.current, opponentGlowRef.current].forEach((el) => {
      if (el) gsap.set(el, { boxShadow: 'none', scale: 1, opacity: 1 });
    });

    const el = turnGlowRef.current || opponentGlowRef.current;
    if (!el) return;

    if (isHandHistorySnapshot) {
      if (isWinner) {
        gsap.set(el, {
          boxShadow: '0 0 24px 8px rgba(98, 139, 72, 0.7), inset 0 0 8px rgba(98, 139, 72, 0.2)',
          scale: 1,
          opacity: 1
        });
      }
    } else if (isWinner) {
      gsap.set(el, {
        boxShadow: '0 0 24px 8px rgba(98, 139, 72, 0.7), inset 0 0 8px rgba(98, 139, 72, 0.2)'
      });
      turnTweenRef.current = gsap.fromTo(
        el,
        { opacity: 0.85, scale: 1 },
        {
          opacity: 1,
          // Subtler breathe (was 1.05) so a winner's ring doesn't visibly
          // crowd the seats either side of it at 390px / in a grid cell.
          scale: 1.03,
          duration: 0.6,
          // Finite on purpose: a repeat:-1 tween keeps GSAP's ticker (and with
          // it the whole WebView frame pipeline) running at refresh rate for as
          // long as the glow is shown. 9 plays (~5.4s) of pulsing, then - since
          // yoyo with an even repeat count ends on the forward pass - the glow
          // parks at its bright state as a steady hold. See
          // docs/investigations/20260819-game-client-overheating.md (8c/8d).
          repeat: reduceAnimations ? 0 : 8,
          yoyo: true,
          ease: 'sine.inOut'
        }
      );
    } else if (isTurn) {
      gsap.set(el, {
        boxShadow: '0 0 24px 8px rgba(233, 140, 38, 0.7), inset 0 0 8px rgba(233, 140, 38, 0.2)'
      });
      // EN-11003: reduced animations show the turn glow steady, without the breathe.
      if (!reduceAnimations)
        turnTweenRef.current = gsap.fromTo(
          el,
          { opacity: 0.85, scale: 1 },
          {
            opacity: 1,
            // Subtler breathe (was 1.05) so a winner's ring doesn't visibly
            // crowd the seats either side of it at 390px / in a grid cell.
            scale: 1.03,
            duration: 0.6,
            // Finite on purpose: a repeat:-1 tween keeps GSAP's ticker (and with
            // it the whole WebView frame pipeline) running at refresh rate for as
            // long as the glow is shown. 9 plays (~5.4s) of pulsing, then - since
            // yoyo with an even repeat count ends on the forward pass - the glow
            // parks at its bright state as a steady hold. See
            // docs/investigations/20260819-game-client-overheating.md (8c/8d).
            repeat: 8,
            yoyo: true,
            ease: 'sine.inOut'
          }
        );
    }

    return () => {
      if (turnTweenRef.current) {
        turnTweenRef.current.kill();
        turnTweenRef.current = null;
      }
      [turnGlowRef.current, opponentGlowRef.current].forEach((el) => {
        if (el) gsap.set(el, { boxShadow: 'none', scale: 1, opacity: 1 });
      });
    };
  }, [isHandHistorySnapshot, isTurn, isWinner, isPortrait, reduceAnimations]);

  // Fade in animation. On arrival the seats are simply already there — every
  // seat fading in at once is pure cost during a table switch.
  useEffect(() => {
    const el = seatRef.current;
    if (isHandHistorySnapshot) {
      if (el) gsap.set(el, { opacity: 1, scale: 1 });
      return;
    }

    if (el) {
      snapOrFromTo(
        shouldSnap(),
        el,
        { opacity: 0, scale: 0.85 },
        { opacity: 1, scale: 1, duration: 0.4, ease: 'power2.out' }
      );
    }
    return () => {
      if (el) gsap.killTweensOf(el);
    };
    // shouldSnap is read inside the effect on purpose — see hooks/useTableSnap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHandHistorySnapshot, seatRef]);
};
